import React, { useState, useEffect } from 'react';
import { X, DollarSign, Tag, AlertCircle } from 'lucide-react';

const FinancialAdjustmentModal = ({ isOpen, onClose, appointment, onSave, type }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && appointment) {
      if (type === 'discount') {
        setAmount(appointment.discount_amount || '');
        setReason(appointment.discount_reason || '');
      } else if (type === 'refund') {
        setAmount(appointment.refund_amount || '');
        setReason(appointment.refund_reason || '');
      }
      setError('');
    }
  }, [isOpen, appointment, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const numAmount = parseFloat(amount);
    
    if (!amount || numAmount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }

    const totalPrice = parseFloat(appointment.total_price) || 0;

    // Validation based on type
    if (type === 'discount') {
      if (numAmount > totalPrice) {
        setError('Discount cannot exceed the total price');
        return;
      }
    } else if (type === 'refund') {
      const amountPaid = parseFloat(appointment.amount_paid) || totalPrice;
      if (numAmount > amountPaid) {
        setError('Refund cannot exceed the amount paid');
        return;
      }
    }

    setLoading(true);
    try {
      await onSave({
        amount: numAmount,
        reason: reason.trim(),
        type
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save adjustment');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Are you sure you want to remove this ${type}?`)) {
      return;
    }

    setLoading(true);
    try {
      await onSave({
        amount: 0,
        reason: '',
        type,
        remove: true
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to remove ${type}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateFinalPrice = () => {
    const totalPrice = parseFloat(appointment?.total_price) || 0;
    const discountAmount = type === 'discount' ? parseFloat(amount) || 0 : parseFloat(appointment?.discount_amount) || 0;
    const refundAmount = type === 'refund' ? parseFloat(amount) || 0 : parseFloat(appointment?.refund_amount) || 0;
    return totalPrice - discountAmount - refundAmount;
  };

  if (!isOpen) return null;

  const isDiscount = type === 'discount';
  const hasExisting = isDiscount 
    ? (appointment?.discount_amount && parseFloat(appointment.discount_amount) > 0)
    : (appointment?.refund_amount && parseFloat(appointment.refund_amount) > 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDiscount ? 'bg-orange-50' : 'bg-red-50'
        }`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${
              isDiscount ? 'bg-orange-100' : 'bg-red-100'
            }`}>
              {isDiscount ? (
                <Tag className={isDiscount ? 'text-orange-600' : 'text-red-600'} size={24} />
              ) : (
                <DollarSign className="text-red-600" size={24} />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isDiscount ? 'Manage Discount' : 'Manage Refund'}
              </h2>
              <p className="text-sm text-gray-600">
                Appointment #{appointment?.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Current Pricing Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Original Price:</span>
              <span className="font-semibold">${parseFloat(appointment?.total_price || 0).toFixed(2)}</span>
            </div>
            {appointment?.discount_amount > 0 && (
              <div className="flex justify-between text-orange-600">
                <span>Current Discount:</span>
                <span className="font-semibold">-${parseFloat(appointment.discount_amount).toFixed(2)}</span>
              </div>
            )}
            {appointment?.refund_amount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Current Refund:</span>
                <span className="font-semibold">-${parseFloat(appointment.refund_amount).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-200">
              <span className="font-semibold text-gray-900">Final Price:</span>
              <span className="font-bold text-primary-600">
                ${calculateFinalPrice().toFixed(2)}
              </span>
            </div>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isDiscount ? 'Discount' : 'Refund'} Amount *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="text-gray-400" size={18} />
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                max={isDiscount ? appointment?.total_price : (appointment?.amount_paid || appointment?.total_price)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input pl-10"
                placeholder="0.00"
                required
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {isDiscount 
                ? `Maximum: $${parseFloat(appointment?.total_price || 0).toFixed(2)}`
                : `Maximum: $${parseFloat(appointment?.amount_paid || appointment?.total_price || 0).toFixed(2)}`
              }
            </p>
          </div>

          {/* Reason Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input"
              rows="3"
              placeholder={isDiscount 
                ? "e.g., Loyal customer discount, Service issue compensation" 
                : "e.g., Service not satisfactory, Appointment cancelled late"
              }
              required
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="text-red-600" size={20} />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 btn ${
                isDiscount 
                  ? 'bg-orange-600 hover:bg-orange-700' 
                  : 'bg-red-600 hover:bg-red-700'
              } text-white disabled:opacity-50`}
            >
              {loading ? 'Saving...' : hasExisting ? 'Update' : 'Apply'}
            </button>
            
            {hasExisting && (
              <button
                type="button"
                onClick={handleRemove}
                disabled={loading}
                className="btn bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
              >
                Remove
              </button>
            )}
            
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinancialAdjustmentModal;
